import api from './api';

// The browse screen pages through the catalog rather than holding it: the
// 2026-2027 catalog is 3,280 courses, so one request for all of them is both
// a slow first paint and a list nobody can scroll.
const PAGE_SIZE = 50;

/**
 * Service for Course/Section data
 * Data sourced from Banner (later Workday)
 */
export const courseService = {
    /**
     * Get all courses for a term
     * @param {string} termCode - Term code (e.g., '202510' for Spring 2025)
     */
    async getCoursesByTerm(
        termCode,
        { limit = PAGE_SIZE, offset = 0, subject, q } = {}
    ) {
        const params = { term: termCode, limit, offset };
        if (subject && subject !== 'all') params.subject = subject;
        // Omitted rather than sent empty: the server reads a blank `q` as
        // absent either way, and leaving it off keeps the request URL the
        // same as an unfiltered page, which is what gets cached and logged.
        if (q && q.trim()) params.q = q.trim();
        const response = await api.get('/course', { params });
        // Server returns `{ courses, total, limit, offset }`. The previous
        // fallback `|| response.data` silently accepted a wrong shape and
        // would have masked any contract regression. Callers need `total` to
        // know whether another page exists, so the envelope is returned whole.
        return response.data;
    },

    /**
     * Every course in a term marked eligible for one program.
     *
     * A coordinator dashboard needs the whole set rather than a page of it,
     * and the set is small: it holds only the courses a coordinator has
     * marked, not the catalog. Pages anyway, because the server caps a
     * response at 100 and a term that grows past that would otherwise lose
     * courses off the end of a list nobody could tell was truncated.
     */
    async getCoursesEligibleForProgram(termCode, program) {
        const pageLimit = 100;
        let offset = 0;
        let all = [];
        // Bounded so a server that reported a total it never delivers cannot
        // spin here forever.
        for (let request = 0; request < 50; request += 1) {
            const response = await api.get('/course', {
                params: {
                    term: termCode,
                    eligibleFor: program,
                    limit: pageLimit,
                    offset,
                },
            });
            const { courses = [], total = 0 } = response.data;
            all = all.concat(courses);
            offset = all.length;
            if (courses.length === 0 || all.length >= total) break;
        }
        return all;
    },

    /**
     * Subject prefixes present in a term's catalog, for the browse filter.
     */
    async getSubjectPrefixes(termCode) {
        const response = await api.get('/course/prefixes', {
            params: { term: termCode },
        });
        return response.data.prefixes;
    },

    /**
     * Free-text course search, served by the API rather than filtered in the
     * browser. `q` accepts a course code ("CHE 1800") or words from a title,
     * or subject.
     */
    async searchCoursesByText(termCode, q, { limit = 25, offset = 0 } = {}) {
        const response = await api.get('/course/search', {
            params: { term: termCode, q, limit, offset },
        });
        return response.data;
    },

    /**
     * Get course by ID
     */
    async getCourseById(courseId) {
        const response = await api.get(`/course/${courseId}`);
        return response.data;
    },

    /**
     * Get sections for a course
     */
    async getSectionsByCourse(courseId) {
        const response = await api.get(`/course/${courseId}/sections`);
        return response.data;
    },

    /**
     * Search courses by explicit field criteria.
     * Returns the paged envelope `{ courses, total, limit, offset }`; the
     * server clamps limit, so a caller cannot ask for the whole catalog.
     * For a single free-text box, use searchCoursesByText.
     * @param {Object} criteria - subject, courseNumber, title, term
     */
    async searchCourses(criteria) {
        const response = await api.get('/course/search', { params: criteria });
        return response.data;
    },

    /**
     * Get courses eligible for peer support
     * Returns courses that have been flagged for SI, TA, or LA support
     */
    async getEligibleCourses(programType, termCode) {
        const response = await api.get('/course/eligible', {
            params: { program: programType, term: termCode },
        });
        return response.data;
    },

    /**
     * Get available terms
     */
    async getAvailableTerms() {
        const response = await api.get('/course/terms');
        return response.data;
    },

    /**
     * Update a course (admin; faculty configuring offering details; or a
     * program coordinator configuring eligibility for their own program)
     */
    async updateCourse(courseId, courseData) {
        const response = await api.put(`/course/${courseId}`, courseData);
        return response.data;
    },

    /**
     * The course behind an application for a course the catalog does not
     * list. Returns the catalog's own record when it has that subject and
     * number for the term, otherwise one added for the applicant. Faculty
     * and admin only; a malformed field is refused with a readable message.
     *
     * @param {{ term: string, subject: string, courseNumber: string,
     *   title: string }} course
     * @returns {Promise<object>} The course, with its _id.
     */
    async createUnlistedCourse({ term, subject, courseNumber, title }) {
        const response = await api.post('/course/unlisted', {
            term,
            subject,
            courseNumber,
            title,
        });
        return response.data;
    },
};

export default courseService;
